import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { Refresh, Previous } from 'grommet-icons';
import Case from 'case';
import uniqid from 'uniqid';
import { useAppTheme } from '@Components/Context';
import { Seo, Spinning, AppButton } from '@Components/Control';
import { AuthNavWrapper } from '@Components/Navigation';
import { NoDataPlaceholder, TableWrapper, renderTableHeader } from '@Components/Partial/Product/SharedComponents.jsx';
import { initiateFetchBulkAssetStatusRequest, clearBulkAssetStatusRequest } from '@Actions';
import { buildDateTimeLabel } from '@Helpers/util';
import { paths } from '@Components/configs';
import { StyledStatusBadge } from '@Components/Partial/Product/ListSearches/StyledListSearches.jsx';
import { STATUS_COLOR_MAP, ASSET_UPLOAD_DETAILS_HEADER_MAP } from '@Components/Partial/Product/ContentId/constants';

const POLL_INTERVAL = 20000;

const AssetUploadDetailsPage = ({
  small,
  location,
  loading,
  fetchBulkAssetStatus,
  clearBulkAssetStatus,
  requestStatusData,
}) => {
  const { id } = useParams();
  const { theme: { page, button }, isDarkMode } = useAppTheme();

  React.useEffect(() => {
    if (id) {
      fetchBulkAssetStatus(id);
      const interval = setInterval(() => fetchBulkAssetStatus(id), POLL_INTERVAL);
      return () => {
        clearBulkAssetStatus();
        clearInterval(interval);
      };
    }
    return () => clearBulkAssetStatus();
  }, [id]);

  const renderAssetTable = (assets) => {
    if (!assets || assets.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={false}
          loading={false}
          label="No assets found for this request."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    const renderDataPoint = (uid, row) => {
      if (uid === 'startedAt' || uid === 'completedAt') {
        return (
          <Text size="1rem" color={page.tableText}>
            {row[uid] ? buildDateTimeLabel(row[uid]) : '—'}
          </Text>
        );
      }

      if (uid === 'status') {
        return (
          <StyledStatusBadge
            background={STATUS_COLOR_MAP[row.status]?.background || page.altBg}
          >
            <Text
              size="1rem"
              color={STATUS_COLOR_MAP[row.status]?.text || page.tableText}
              weight={600}
            >
              {Case.capital(row.status)}
            </Text>
          </StyledStatusBadge>
        );
      }

      if (uid === 'errorMessage') {
        return (
          <Text size="1rem" color={row[uid] ? 'var(--app-error)' : page.tableText}>
            {row[uid] || '—'}
          </Text>
        );
      }

      return (
        <Text size="1rem" color={page.tableText}>
          {row[uid] || '—'}
        </Text>
      );
    };

    return (
      <TableWrapper
        hidePagination
        small={small}
        header="Asset Statuses"
        boxPad={small ? '0.5rem' : '1rem'}
        isPaged={false}
        tableControls={null}
        width="100%"
      >
        <Box width="100%" direction="column">
          {!small && (
            <Box
              width="100%"
              wrap={false}
              direction="row"
              border={{
                color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
              }}
            >
              {ASSET_UPLOAD_DETAILS_HEADER_MAP.map(({ uid, display, size }) => (
                <Box
                  key={uid}
                  width={size}
                  pad={{
                    horizontal: '0.5rem',
                    top: '1rem',
                    bottom: '0.5rem',
                  }}
                >
                  {renderTableHeader(display, small)}
                </Box>
              ))}
            </Box>
          )}
          <Box
            width="100%"
            direction="column"
            style={{
              maxHeight: '50vh',
              overflowY: 'auto',
            }}
          >
            {assets.map((row, i) => (
              <Box
                key={row.requestId || uniqid()}
                direction={small ? 'column' : 'row'}
                align={small ? 'start' : 'center'}
                border={i < assets.length - 1 && {
                  color: page.tableBorder,
                  size: '1px',
                  style: 'solid',
                  side: 'bottom',
                }}
                height={{ min: 'fit-content' }}
                pad={{ vertical: '1rem', right: '0.5rem' }}
                background={page.tableBg}
                width="100%"
              >
                {ASSET_UPLOAD_DETAILS_HEADER_MAP.map(({ uid, display, size }) => (
                  <Box
                    key={uid}
                    width={small ? '100%' : size}
                    pad={{
                      horizontal: small ? '0' : '0.5rem',
                      vertical: small ? '0.25rem' : '0',
                    }}
                    direction="row"
                    gap="0.5rem"
                  >
                    {small && display && (
                      <Text size="0.875rem" color={page.tableHeader}>
                        {display}
                        :
                      </Text>
                    )}
                    {renderDataPoint(uid, row)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </TableWrapper>
    );
  };

  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  const renderPageContent = () => {
    if (loading) {
      return (
        <Box flex align="center" justify="center" background={usePageBg}>
          <Spinning size="large" color={page.highlight} />
        </Box>
      );
    }

    if (!loading && !requestStatusData) {
      return (
        <Box flex align="center" background={usePageBg} pad={small ? '1.5rem' : '2rem'}>
          <NoDataPlaceholder
            loading={false}
            width="100%"
            label="Upload request could not be found."
            textColor={page.primaryText}
            loaderColor={page.highlight}
          />
        </Box>
      );
    }

    const leftInfo = [
      { label: 'Request ID', value: requestStatusData.requestId },
      { label: 'Asset Label', value: requestStatusData.assetLabel },
      {
        label: 'Status',
        value: (
          <StyledStatusBadge
            background={STATUS_COLOR_MAP[requestStatusData.status]?.background || usePageBg}
          >
            <Text
              size="1rem"
              color={STATUS_COLOR_MAP[requestStatusData.status]?.text || page.primaryText}
              weight={600}
            >
              {Case.capital(requestStatusData.status)}
            </Text>
          </StyledStatusBadge>
        ),
      },
      { label: 'Started At', value: buildDateTimeLabel(requestStatusData.startedAt) },
      { label: 'Completed At', value: buildDateTimeLabel(requestStatusData.completedAt) },
    ].filter(Boolean);

    const summary = requestStatusData.summary || {};
    const assets = requestStatusData.assets || [];

    return (
      <Box
        flex
        direction="column"
        width="100%"
        align="start"
        background={usePageBg}
        pad={{ bottom: small ? '1.5rem' : '2rem' }}
      >
        {/* Top: Request Info and Summary */}
        <Box
          direction={small ? 'column' : 'row'}
          pad="1.5rem"
          gap="1.5rem"
          width="100%"
          align={small ? 'start' : 'stretch'}
        >
          <Box
            flex="grow"
            direction="column"
            gap="0.75rem"
            pad="1rem"
            background={useSectionBg}
            round="var(--app-radius)"
            width={small ? '100%' : { max: '50%' }}
            style={{ minWidth: 0, boxShadow: 'var(--elevation)' }}
          >
            <Text size="1.25rem" weight={600} color={page.primaryText}>
              Request Info
            </Text>
            <Box direction="column" gap="0.5rem">
              {leftInfo.map(({ label, value }) => (
                <Box key={label} direction="row" gap="0.5rem" align="center">
                  <Text weight={500} color={page.primaryText}>
                    {label}
                    :
                  </Text>
                  <Text color={page.primaryText} size="1rem">{value || '—'}</Text>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            flex="grow"
            direction="column"
            gap="0.75rem"
            pad="1rem"
            background={useSectionBg}
            round="var(--app-radius)"
            width={small ? '100%' : { max: '50%' }}
            style={{ minWidth: 0, boxShadow: 'var(--elevation)' }}
          >
            <Text size="1.25rem" weight={600} color={page.primaryText}>
              Summary
            </Text>
            <Box direction="column" gap="0.5rem">
              {Object.entries(summary).length > 0 ? Object.entries(summary).filter(([k]) => k !== 'quotaHeld').map(([k, v]) => (
                <Box key={k} direction="row" gap="0.5rem" align="center">
                  <StyledStatusBadge background={STATUS_COLOR_MAP[k]?.background || usePageBg}>
                    <Text size="1rem" color={STATUS_COLOR_MAP[k]?.text || page.primaryText} weight={600}>
                      {Case.capital(k)}
                    </Text>
                  </StyledStatusBadge>
                  <Text color={page.primaryText}>{v}</Text>
                </Box>
              )) : (
                <Text color={page.primaryText}>—</Text>
              )}
            </Box>
          </Box>
        </Box>
        {/* Asset Statuses Table */}
        {renderAssetTable(assets)}
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Upload status & details',
        subTitle: 'Content ID',
        bannerChildrenPosition: 'bottom',
        bannerChildren: !loading && (
          <Box width="fit-content" direction="row" gap="1rem">
            <AppButton
              overrideHover
              href={paths.productAssetUpload}
              label="Go back"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Previous size="0.8rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
            />
            <AppButton
              overrideHover
              onClick={() => fetchBulkAssetStatus(id)}
              label="Refresh"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Refresh size="0.9rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      {renderPageContent()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    requestStatusData: state.contentIdAssets.bulkAssetRequestStatus,
    loading: state.fetchLoader.secondaryDataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchBulkAssetStatus: initiateFetchBulkAssetStatusRequest,
    clearBulkAssetStatus: clearBulkAssetStatusRequest,
  }, dispatch);
}

AssetUploadDetailsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  fetchBulkAssetStatus: PropTypes.func.isRequired,
  clearBulkAssetStatus: PropTypes.func.isRequired,
  requestStatusData: PropTypes.shape({
    requestId: PropTypes.string.isRequired,
    assetLabel: PropTypes.string,
    status: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    startedAt: PropTypes.string,
    completedAt: PropTypes.string,
    summary: PropTypes.object,
    assets: PropTypes.arrayOf(PropTypes.shape({
      assetId: PropTypes.string,
      assetLabel: PropTypes.string,
      status: PropTypes.string,
      createdAt: PropTypes.string,
      startedAt: PropTypes.string,
      completedAt: PropTypes.string,
      errorMessage: PropTypes.string,
      fileName: PropTypes.string,
      youtubeAssetId: PropTypes.string,
    })),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(AssetUploadDetailsPage);
